/**
 * WsSession — OkraSession adapter that routes `stream()` over an existing
 * WebSocket connection instead of HTTP SSE.
 *
 * All non-streaming methods delegate to the inner HTTP-backed session.
 * Only `stream()` is overridden to send CHAT_COMPLETION over WS and yield
 * CompletionEvents from CHAT_STREAM_* responses.
 *
 * Usage:
 *   const wsSession = new WsSession(httpSession, { send, subscribe });
 *   const chat = useChat({ session: wsSession });
 */

import { Data, Effect, Queue, Stream } from 'effect';

import type {
  OkraSession,
  CompletionEvent,
  CompletionOptions,
  DocumentStatus,
  WaitOptions,
  Page,
  EntitiesResponse,
  QueryResult,
  PublishResult,
  ShareLinkOptions,
  ShareLinkResult,
  DocumentAsset,
  GenerateOptions,
  GenerateResult,
  SessionState,
  StructuredSchema,
} from './types';

// Chat stream event types — mirrors @okrapdf/schemas ChatStreamServerEvent
// Inlined to avoid adding @okrapdf/schemas as a dependency of the published SDK.

export type ChatStreamServerEvent =
  | { type: 'CHAT_STREAM_START'; requestId: string }
  | { type: 'CHAT_STREAM_DELTA'; requestId: string; delta: string }
  | {
      type: 'CHAT_STREAM_DONE';
      requestId: string;
      usage?: { inputTokens: number; outputTokens: number; costUsd: number };
      model?: string;
    }
  | { type: 'CHAT_STREAM_ERROR'; requestId: string; error: string };

/** Function that sends a JSON string over the WebSocket. Returns false if WS is closed. */
export type WsSendFn = (message: string) => boolean;

/** Subscribe to chat stream events. Returns unsubscribe function. */
export type WsSubscribeFn = (
  requestId: string,
  handler: (event: ChatStreamServerEvent) => void,
) => () => void;

export interface WsSessionOptions {
  /** Send JSON string over the existing WebSocket connection */
  send: WsSendFn;
  /** Subscribe to CHAT_STREAM_* events for a given requestId */
  subscribe: WsSubscribeFn;
}

export type WsSessionStreamOperation = 'request-id' | 'subscribe' | 'send';

export class WsSessionStreamError extends Data.TaggedError('WsSessionStreamError')<{
  readonly operation: WsSessionStreamOperation;
  readonly cause: unknown;
}> {}

function tryWsSessionOperation<A>(
  operation: WsSessionStreamOperation,
  evaluate: () => A,
): Effect.Effect<A, WsSessionStreamError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => new WsSessionStreamError({ operation, cause }),
  });
}

/** Restore the failure caught at the Promise/async-iterator compatibility edge. */
export function wsSessionStreamFailureCause(error: unknown): unknown {
  return error instanceof WsSessionStreamError ? error.cause : error;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Effect-native WebSocket completion stream.
 *
 * Subscription and abort resources live in the stream scope. The subscription
 * is installed before the request is sent so a synchronous server response
 * cannot be lost between those two operations.
 */
export function createWsCompletionStream(
  wsOptions: WsSessionOptions,
  query: string,
  options?: CompletionOptions,
): Stream.Stream<CompletionEvent, WsSessionStreamError> {
  return Stream.callback<CompletionEvent, WsSessionStreamError>((queue) =>
    Effect.gen(function* () {
      const requestId = yield* tryWsSessionOperation('request-id', uuid);
      const signal = options?.signal;

      if (signal?.aborted) {
        Queue.endUnsafe(queue);
        return;
      }

      let finished = false;
      let fullText = '';

      const finish = () => {
        if (finished) return;
        finished = true;
        Queue.endUnsafe(queue);
      };

      const handleEvent = (event: ChatStreamServerEvent) => {
        if (finished) return;

        switch (event.type) {
          case 'CHAT_STREAM_START':
            return;

          case 'CHAT_STREAM_DELTA':
            fullText += event.delta;
            Queue.offerUnsafe(queue, { type: 'text_delta', text: event.delta });
            return;

          case 'CHAT_STREAM_DONE':
            Queue.offerUnsafe(queue, { type: 'done', answer: fullText });
            finish();
            return;

          case 'CHAT_STREAM_ERROR':
            Queue.offerUnsafe(queue, { type: 'error', message: event.error });
            finish();
            return;
        }
      };

      yield* Effect.acquireRelease(
        tryWsSessionOperation('subscribe', () => wsOptions.subscribe(requestId, handleEvent)),
        (unsubscribe) =>
          Effect.sync(() => {
            finished = true;
            unsubscribe();
          }),
      );

      if (signal) {
        yield* Effect.acquireRelease(
          Effect.sync(() => {
            const onAbort = () => finish();
            signal.addEventListener('abort', onAbort, { once: true });
            if (signal.aborted) onAbort();
            return onAbort;
          }),
          (onAbort) =>
            Effect.sync(() => {
              signal.removeEventListener('abort', onAbort);
            }),
        );
      }

      if (finished) return;

      const sent = yield* tryWsSessionOperation('send', () =>
        wsOptions.send(
          JSON.stringify({
            type: 'CHAT_COMPLETION',
            requestId,
            messages: [{ role: 'user', content: query }],
          }),
        ),
      );

      if (!sent) {
        Queue.offerUnsafe(queue, { type: 'error', message: 'WebSocket not connected' });
        finish();
      }
    }).pipe(Effect.catch((error) => Queue.fail(queue, error))),
  );
}

export class WsSession implements OkraSession {
  readonly id: string;
  readonly modelEndpoint: string;
  readonly model?: string;

  #inner: OkraSession;
  #opts: WsSessionOptions;

  constructor(inner: OkraSession, opts: WsSessionOptions) {
    this.#inner = inner;
    this.#opts = opts;
    this.id = inner.id;
    this.modelEndpoint = inner.modelEndpoint;
    this.model = inner.model;
  }

  // ── Overridden: stream via WebSocket ──────────────────────────────────────

  async *stream(query: string, options?: CompletionOptions): AsyncGenerator<CompletionEvent> {
    try {
      for await (const event of Stream.toAsyncIterable(
        createWsCompletionStream(this.#opts, query, options),
      )) {
        yield event;
      }
    } catch (error) {
      throw wsSessionStreamFailureCause(error);
    }
  }

  // ── Delegated to inner HTTP session ───────────────────────────────────────

  state(): SessionState {
    return this.#inner.state();
  }
  setModel(model: string): Promise<void> {
    return this.#inner.setModel(model);
  }
  status(signal?: AbortSignal): Promise<DocumentStatus> {
    return this.#inner.status(signal);
  }
  wait(options?: WaitOptions): Promise<DocumentStatus> {
    return this.#inner.wait(options);
  }
  pages(options?: { range?: string; signal?: AbortSignal }): Promise<Page[]> {
    return this.#inner.pages(options);
  }
  page(pageNumber: number, signal?: AbortSignal): Promise<Page> {
    return this.#inner.page(pageNumber, signal);
  }
  entities(options?: {
    type?: string;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<EntitiesResponse> {
    return this.#inner.entities(options);
  }
  downloadUrl(): string {
    return this.#inner.downloadUrl();
  }
  query(sql: string, signal?: AbortSignal): Promise<QueryResult> {
    return this.#inner.query(sql, signal);
  }
  logs(options?: import('./types').LogsOptions): Promise<import('./types').LogEntry[]> {
    return this.#inner.logs(options);
  }
  publish(signal?: AbortSignal): Promise<PublishResult> {
    return this.#inner.publish(signal);
  }
  shareLink(options?: ShareLinkOptions): Promise<ShareLinkResult> {
    return this.#inner.shareLink(options);
  }
  assets(signal?: AbortSignal): Promise<DocumentAsset[]> {
    return this.#inner.assets(signal);
  }
  asset<T = unknown>(assetId: string, signal?: AbortSignal): Promise<DocumentAsset<T> | null> {
    return this.#inner.asset<T>(assetId, signal);
  }

  prompt(
    query: string,
    options?: GenerateOptions & { schema?: undefined },
  ): Promise<GenerateResult>;
  prompt<T>(
    query: string,
    options: GenerateOptions & { schema: StructuredSchema<T> },
  ): Promise<GenerateResult<T>>;
  prompt<T = undefined>(query: string, options?: GenerateOptions): Promise<GenerateResult<T>> {
    if (options?.schema !== undefined) {
      return this.#inner.prompt(
        query,
        options as GenerateOptions & { schema: StructuredSchema<unknown> },
      ) as Promise<GenerateResult<T>>;
    }
    return this.#inner.prompt(
      query,
      options as GenerateOptions & { schema?: undefined },
    ) as Promise<GenerateResult<T>>;
  }
}
